import React, { useEffect, useRef, useState } from 'react';
import FormErrorsBox from '../FormErrorsBox';
import useApiCall from '../../hooks/useApiCall';
import { useGlobalProfile } from '../../hooks/useGlobalProfile';
import { MonthlyDataFormErrorClasses, MonthlyData } from '../../Interfaces';

type MonthlyDataFormProps = {
  formMode: string;
  formDone: () => void;
  formSaved: () => void;
  targetYear: number;
  targetMonth: number;
  editData?: MonthlyData;
};

function MonthlyDataForm(props: MonthlyDataFormProps): React.ReactElement {
  const { hasToken, isLoading, data, execute } = useApiCall();
  const { profile: globalProfile } = useGlobalProfile();
  const [hideErrorBox, setHideErrorBox] = useState('hide');
  const formRef = useRef<HTMLFormElement>(null);
  const { formDone, formSaved, formMode, editData, targetYear, targetMonth } =
    props;
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    totalSales: formMode === 'update' && editData ? editData.totalSales : 0,
    squareFootage: editData?.squareFootage || 0,
    leaseAmount: editData?.leaseAmount || 0,
  });
  const [errorClass, setErrorClass] = useState<MonthlyDataFormErrorClasses>({
    totalSales: '',
    squareFootage: '',
    leaseAmount: '',
  });

  const resetAndCloseForm = (): void => {
    setHideErrorBox('hide');
    setErrorClass({
      totalSales: '',
      squareFootage: '',
      leaseAmount: '',
    });
    setFormErrors([]);
    formDone();
  };

  //
  // function: handleFormChange
  // desc: react to user changing values in the input fields of form.
  //        save values to state object for use when form is submitted
  //
  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value.replaceAll(',', ''),
    });
  };

  //
  // function: handleFormSubmit
  // desc: react to user submitting the form. validate input and then make API call
  //       to store it
  //
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    // BEGIN form data validation
    let errorsFound = false;
    const localErrorArr: string[] = [];

    const localErrorClasses: MonthlyDataFormErrorClasses = {
      totalSales: '',
      squareFootage: '',
      leaseAmount: '',
    };

    const errorMessages = {
      totalSales:
        'Total Sales is required and must be a number greater than 0.',
      squareFootage:
        'Square Footage is required and must be a number greater than 0.',
      leaseAmount:
        'Lease Amount is required and must be a number greater than 0.',
    };

    ['totalSales', 'squareFootage', 'leaseAmount'].forEach(formItem => {
      let itemError = false;
      const formValue = formData[formItem as keyof unknown] as string;

      if (!formValue.toString().match(/^[0-9]*\.?[0-9]+$/)) {
        itemError = true;
      }

      if (parseFloat(formValue) <= 0) {
        itemError = true;
      }

      if (itemError) {
        errorsFound = true;
        localErrorArr.push(errorMessages[formItem as keyof unknown]);
        localErrorClasses[formItem as keyof MonthlyDataFormErrorClasses] =
          'has-error';
      }
    });

    if (errorsFound) {
      setHideErrorBox('');
    } else {
      setHideErrorBox('hide');
    }
    setErrorClass({ ...errorClass, ...localErrorClasses });
    setFormErrors(localErrorArr);
    // END form data validation

    if (hasToken && !errorsFound) {
      setIsSaving(true);

      if (formMode === 'add') {
        execute({
          method: 'POST',
          endpoint: `/api/establishments/${globalProfile.activeEstablishmentId}/data`,
          requestBody: [
            {
              establishmentId: globalProfile.activeEstablishmentId,
              month: targetMonth,
              year: targetYear,
              squareFootage: parseFloat(formData.squareFootage.toString()),
              leaseAmount: parseFloat(formData.leaseAmount.toString()),
              totalSales: parseFloat(formData.totalSales.toString()),
            },
          ],
        });
      } else {
        execute({
          method: 'PUT',
          endpoint: `/api/establishments/${globalProfile.activeEstablishmentId}/data`,
          requestBody: [
            {
              id: editData?.id,
              establishmentId: globalProfile.activeEstablishmentId,
              month: targetMonth,
              year: targetYear,
              squareFootage: parseFloat(formData.squareFootage.toString()),
              leaseAmount: parseFloat(formData.leaseAmount.toString()),
              totalSales: parseFloat(formData.totalSales.toString()),
            },
          ],
        });
      }
    }
  };

  useEffect(() => {
    setFormData({
      totalSales: formMode === 'update' && editData ? editData.totalSales : 0,
      squareFootage: editData?.squareFootage || 0,
      leaseAmount: editData?.leaseAmount || 0,
    });

    if (isSaving) {
      if (!isLoading) {
        if (
          data.status === 'NOT_ACCEPTABLE' ||
          data.status === 'NOT_FOUND' ||
          data.status === 'CONFLICT'
        ) {
          // We got an error in the API call
          setHideErrorBox('');
          setFormErrors([data.message || '']);
          setIsSaving(false);
        } else {
          if (formRef.current) {
            formRef.current.reset();
          }
          setIsSaving(false);
          formSaved();
        }
      }
    }
  }, [editData, isLoading, targetMonth]);

  return (
    <div>
      <FormErrorsBox formErrors={formErrors} hideErrorBoxClass={hideErrorBox} />
      <form onSubmit={handleFormSubmit} ref={formRef}>
        <div className={`form-group row ${errorClass.totalSales}`}>
          <label htmlFor="totalSales" className="col-sm-5 col-form-label">
            Total Sales
          </label>
          <div className="col-sm-7">
            <input
              type="text"
              className="form-control"
              id="totalSales"
              name="totalSales"
              placeholder="Total Sales"
              value={formData.totalSales === 0 ? '' : formData.totalSales}
              onChange={handleFormChange}
            />
          </div>
        </div>
        <div className={`form-group row ${errorClass.leaseAmount}`}>
          <label htmlFor="leaseAmount" className="col-sm-5 col-form-label">
            Lease Amount
          </label>
          <div className="col-sm-7">
            <input
              type="text"
              className="form-control"
              id="leaseAmount"
              name="leaseAmount"
              placeholder="Lease Amount"
              value={formData.leaseAmount === 0 ? '' : formData.leaseAmount}
              onChange={handleFormChange}
            />
          </div>
        </div>
        <div className={`form-group row ${errorClass.squareFootage}`}>
          <label htmlFor="squareFootage" className="col-sm-5 col-form-label">
            Square Footage
          </label>
          <div className="col-sm-7">
            <input
              type="text"
              className="form-control"
              id="squareFootage"
              name="squareFootage"
              placeholder="Square Footage"
              value={formData.squareFootage === 0 ? '' : formData.squareFootage}
              onChange={handleFormChange}
            />
          </div>
        </div>
        <div className="form-group row">
          <div className="col-sm-12 text-right">
            <span>
              <button
                onClick={resetAndCloseForm}
                type="button"
                disabled={isSaving}
                className="btn"
              >
                <div
                  style={{
                    float: 'right',
                    paddingTop: '2px',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                  }}
                >
                  Cancel
                </div>
              </button>
              &nbsp;
            </span>
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-primary"
            >
              {(() => {
                if (isSaving) {
                  return (
                    <span>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <div
                        style={{
                          float: 'right',
                          paddingTop: '2px',
                          paddingLeft: '4px',
                        }}
                      >
                        &nbsp;Saving...
                      </div>
                    </span>
                  );
                }

                return (
                  <div
                    style={{
                      float: 'right',
                      paddingTop: '2px',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                    }}
                  >
                    &nbsp;Save
                  </div>
                );
              })()}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default MonthlyDataForm;
